import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CalendarMonth,
} from '@mui/icons-material';
import { holidayAPI } from '../../services/api';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    holiday_date: '',
    reason: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadHolidays();
  }, []);

  const loadHolidays = async () => {
    try {
      const response = await holidayAPI.getAll();
      const sortedHolidays = response.data.sort((a, b) => 
        new Date(a.holiday_date) - new Date(b.holiday_date)
      );
      setHolidays(sortedHolidays);
    } catch (error) {
      setError('Failed to load holidays');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.holiday_date || !formData.reason) {
        setError('Please fill all required fields');
        return;
      }

      if (editing) {
        await holidayAPI.update(editing.holiday_id, formData);
        setMessage('Holiday updated successfully');
      } else {
        await holidayAPI.create(formData);
        setMessage('Holiday created successfully');
      }
      setOpen(false);
      loadHolidays();
      resetForm();
      setError('');
    } catch (error) {
      setError(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this holiday? (Soft delete)')) {
      try {
        await holidayAPI.delete(id);
        setMessage('Holiday deleted successfully');
        loadHolidays();
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to delete holiday');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      holiday_date: '',
      reason: '',
    });
    setEditing(null);
    setError('');
  };

  const handleEdit = (holiday) => {
    setEditing(holiday);
    setFormData({
      holiday_date: holiday.holiday_date,
      reason: holiday.reason,
    });
    setOpen(true);
  };

  const getMonthYear = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long'
    });
  };

  const isUpcoming = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const holidayDate = new Date(dateString);
    return holidayDate >= today;
  };

  const groupedHolidays = holidays.reduce((groups, holiday) => {
    const monthYear = getMonthYear(holiday.holiday_date);
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(holiday);
    return groups;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Holiday Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Add Holiday
        </Button>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Alert severity="info" icon={<CalendarMonth />}>
          Total Holidays: {holidays.length} | 
          Upcoming: {holidays.filter(h => isUpcoming(h.holiday_date)).length}
        </Alert>
      </Box>

      {Object.keys(groupedHolidays).length === 0 ? (
        <Alert severity="info">No holidays configured. Add holidays using the button above.</Alert>
      ) : (
        Object.entries(groupedHolidays).map(([monthYear, monthHolidays]) => (
          <Box key={monthYear} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              {monthYear}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Day</TableCell>
                    <TableCell>Holiday Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthHolidays.map((holiday) => {
                    const date = new Date(holiday.holiday_date);
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                    const dateFormatted = date.toLocaleDateString('en-US', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    });

                    return (
                      <TableRow key={holiday.holiday_id}>
                        <TableCell>{dateFormatted}</TableCell>
                        <TableCell>{dayName}</TableCell>
                        <TableCell>{holiday.reason}</TableCell>
                        <TableCell>
                          <Chip 
                            label={isUpcoming(holiday.holiday_date) ? 'Upcoming' : 'Past'} 
                            color={isUpcoming(holiday.holiday_date) ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleEdit(holiday)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(holiday.holiday_id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            required
            type="date"
            label="Holiday Date"
            value={formData.holiday_date}
            onChange={(e) => setFormData({ ...formData, holiday_date: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            required
            label="Holiday Name / Reason"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            placeholder="e.g., New Year's Day, Diwali, Independence Day"
            helperText="Enter the name or reason for the holiday"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HolidayManagement;
